<template>
    <div class="related-links-admin">

        <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
        </div>

        <div class="related-links-admin__toolbar admin-edit-only">
            <button v-if="canConfigure"
                    type="button"
                    class="btn btn-secondary"
                    @click="showSettings = !showSettings">
                Widget settings
            </button>

            <button v-if="canEditLinks"
                    type="button"
                    class="btn btn-primary"
                    @click="showCreateGroup = true">
                <i class="fa-solid fa-plus" aria-hidden="true"></i>
                Add group
            </button>
        </div>

        <form v-if="showCreateGroup" class="related-links-admin__new-group" @submit.prevent="createGroup">
            
            <div class="d-inline-flex">
                <input v-model.trim="newGroupName" type="text" class="form-control" placeholder="Group name">

                <button type="button" class="btn btn-link" @click="cancelCreateGroup">
                    Cancel
                </button>

                <button type="submit" class="btn btn-primary">
                    Save
                </button>
            </div>
        </form>

        <div v-if="showSettings" class="related-links-admin__settings">
            <div class="form-group">
                <label>
                    What should show if there are no links?
                </label>

                <input v-model="settingsDraft.NoLinksText" type="text" class="form-control">
            </div>

            <div v-for="setting in widgetSettings"
                 :key="setting.key"
                 class="mb-3">

                <div v-if="setting.type === 'checkbox'"
                     class="form-check">
                    <input :id="`setting-${setting.key}`"
                           v-model="settingsDraft[setting.key]"
                           type="checkbox"
                           class="form-check-input"
                           :disabled="setting.disabled?.(settingsDraft) ?? false">

                    <label class="form-check-label"
                           :for="`setting-${setting.key}`">
                        {{ setting.label }}
                    </label>
                </div>

                <div v-else-if="setting.type === 'text'"
                     class="form-group">
                    <label :for="`setting-${setting.key}`">
                        {{ setting.label }}
                    </label>

                    <input :id="`setting-${setting.key}`"
                           v-model.trim="settingsDraft[setting.key]"
                           type="text"
                           class="form-control"
                           :placeholder="setting.placeholder ?? ''">
                </div>

            </div>

            <div class="mt-3 text-center">
                <button type="button" class="btn btn-link" @click="cancelSettings">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click="saveSettings">
                    Save settings
                </button>
            </div>
        </div>

        <RelatedLinksDisplay :widget="widget"
                             :show-unpublished="showUnpublished"
                             :draggable-groups="widget.Groups.length > 1"
                             draggable-links
                             @group-drag-start="beginGroupDrag"
                             @group-drag-end="endGroupDrag"
                             @link-drag-start="beginLinkDrag"
                             @link-drag-end="endLinkDrag"
                             @link-added="moveLinkToGroup">

            <template #group-title="{ group }">
                <form v-if="editingGroupId === group.Id" class="related-links-admin__group-editor" @submit.prevent="saveGroupName(group)">

                    <div class="d-inline-flex">
                        <input v-model.trim="groupNameDraft" type="text" class="form-control" placeholder="Add Group Title">

                        <button type="button" class="btn btn-link" @click="cancelGroupEdit">
                            Cancel
                        </button>

                        <button type="submit" class="btn btn-primary">
                            Save
                        </button>
                    </div>
                </form>

                <h3 v-else-if="group.Name">
                    {{ group.Name }}
                </h3>

            </template>

            <template #group-actions="{ group }">
                <div v-if="canEditLinks" class="related-links-admin__group-actions admin-edit-only">

                    <button type="button"
                            class="btn btn-primary related-links-admin__icon-button"
                            title="Add link"
                            @click="beginCreateLink(group)">
                        <i class="fa-solid fa-plus" aria-hidden="true"></i> Add Link
                        <span class="sr-only">Add link</span>
                    </button>

                    <button v-if="widget.Groups.length > 1"
                            type="button"
                            class="btn btn-link related-links-admin__icon-button related-links-admin__group-drag-handle"
                            title="Drag to reorder group">
                        <i class="fa-solid fa-grip-vertical" aria-hidden="true"></i>
                        <span class="sr-only">Drag to reorder group</span>
                    </button>
                    
                    <button type="button"
                            class="btn btn-link related-links-admin__icon-button"
                            title="Rename group"
                            @click="beginGroupEdit(group)">
                        <i class="fa-solid fa-pen" aria-hidden="true"></i>
                        <span class="sr-only">Rename group</span>
                    </button>

                    <button type="button"
                            class="btn btn-link text-danger"
                            @click="deleteGroup(group)">
                        <i class="fa-solid fa-trash" aria-hidden="true"></i>
                        <span class="sr-only">Delete</span>
                    </button>

                </div>
            </template>

            <template #link="{ group, link }">
                <RelatedLinkEditor v-if="editingPlacementId === link.Id"
                                   :link="link"
                                   :saving="savingLink"
                                   :page-id="widget.PageId"
                                   :related-links-base-url="relatedLinksBaseUrl"
                                   :file-upload-options="fileUploadOptions"
                                   :upload-folder="widget.Settings?.UploadFolder || 'pdfs'"
                                   @save="saveLink(group, link, $event)"
                                   @cancel="cancelLinkEdit" />

                <RelatedLinkItem v-else :link="link">

                    <template #actions>
                        <div class="related-links-admin__item-actions admin-edit-only">
                            
                            <span v-if="getLinkPublishStatus(link) === 'scheduled'" class="btn">
                                <i class="fa-regular fa-clock"
                                   title="Scheduled — not currently visible to the public"
                                   aria-label="Scheduled"></i>
                            </span>

                            <span v-else-if="getLinkPublishStatus(link) === 'expired'" class="btn">
                                <i class="fa-regular fa-calendar-xmark"
                                    title="Expired — no longer visible to the public"
                                    aria-label="Expired"></i>
                            </span>                               

                            <button type="button"
                                    class="btn btn-link related-links-admin__icon-button related-links-admin__link-drag-handle"
                                    title="Drag to reorder">
                                <i class="fa-solid fa-grip-vertical" aria-hidden="true"></i>
                                <span class="sr-only">Drag to reorder link</span>
                            </button>

                            <button type="button"
                                    class="btn btn-link related-links-admin__icon-button"
                                    title="Edit"
                                    @click="beginLinkEdit(link)">
                                <i class="fa-solid fa-pen" aria-hidden="true"></i>
                                <span class="sr-only">Edit</span>
                            </button>

                            <button type="button"
                                    class="btn btn-link related-links-admin__icon-button text-danger"
                                    title="Delete"
                                    @click="deleteLink(group, link)">
                                <i class="fa-solid fa-trash" aria-hidden="true"></i>
                                <span class="sr-only">Delete</span>
                            </button>
                        </div>
                    </template>


                </RelatedLinkItem>
            </template>

            <template #before-links="{ group }">
                <RelatedLinkEditor v-if="creatingLinkGroupId === group.Id"
                                   :saving="savingLink"
                                   :page-id="widget.PageId"
                                   :related-links-base-url="relatedLinksBaseUrl"
                                   :file-upload-options="fileUploadOptions"
                                   :upload-folder="widget.Settings?.UploadFolder || 'pdfs'"
                                   @save="createLink(group, $event)"
                                   @cancel="cancelCreateLink" />
            </template>

        </RelatedLinksDisplay>
    </div>
</template>

<script setup>
    import {
        onBeforeUnmount,
        onMounted,
        computed,
        reactive,
        ref,
        watch
    } from 'vue';

    import RelatedLinksDisplay from './RelatedLinksDisplay.vue';
    import RelatedLinkItem from './RelatedLinkItem.vue';
    import RelatedLinkEditor from './RelatedLinkEditor.vue';

    import {
        joinUrl,
        requestJson,
        getLinkPublishStatus
    } from './helpers';

    const props = defineProps({
        initialWidget: {
            type: Object,
            required: true
        },

        relatedLinksBaseUrl: {
            type: String,
            required: true
        },

        fileUploadOptions: {
            type: Object,
            required: true
        },

        capabilities: {
            type: Object,
            default: () => ({
                CanEditLinks: true,
                CanConfigure: true
            })
        }
    });

    const showUnpublished = ref(document.body.classList.contains('edit-mode'));
    let editModeObserver = null;

    const widget = ref(
        structuredClone(props.initialWidget)
    );

    const errorMessage = ref('');

    const editingPlacementId = ref(null);
    const creatingLinkGroupId = ref(null);
    const savingLink = ref(false);

    const editingGroupId = ref(null);
    const groupNameDraft = ref('');

    const showCreateGroup = ref(false);
    const newGroupName = ref('');

    const showSettings = ref(false);

    const settingsDraft = reactive({
        UploadFolder: 'pdfs',
        ...(widget.value.Settings ?? {})
    });

    const canEditLinks = computed(() =>
        props.capabilities.CanEditLinks === true
    );

    const canConfigure = computed(() =>
        props.capabilities.CanConfigure === true
    );

    const widgetSettings = [
        { key: 'ShowFilter', label: 'Show filter', type: 'checkbox' },
        { key: 'IsCollapsible', label: 'Collapsible groups', type: 'checkbox' },
        { key: 'IsAccordion', label: 'Accordion', type: 'checkbox', disabled: draft => !draft.IsCollapsible },
        { key: 'ShowCollapseAll', label: 'Show expand/collapse all button', disabled: draft => !draft.IsCollapsible, type: 'checkbox' },
        { key: 'ExpandFirstGroup', label: 'Expand first group initially', disabled: draft => !draft.IsCollapsible, type: 'checkbox' },
        { key: 'UploadFolder', label: 'Upload Folder', type: 'text', placeholder: 'pdfs' }
    ];

    let linkDragSnapshot = null;
    let groupDragSnapshot = null;

    onMounted(() => {
        editModeObserver = new MutationObserver(() => {
            showUnpublished.value = document.body.classList.contains('edit-mode');
        });

        editModeObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class']
        });
    });

    onBeforeUnmount(() => {
        editModeObserver?.disconnect();
    });

    watch(
        () => settingsDraft.IsCollapsible,
        isCollapsible => {
            if (!isCollapsible) {
                settingsDraft.IsAccordion = false;
                settingsDraft.ShowCollapseAll = false;
                settingsDraft.ExpandFirstGroup = false;
            }
        }
    );

    function clearError() {
        errorMessage.value = '';
    }

    function setError(error) {
        errorMessage.value =
            error?.message ||
            'Unable to save the requested change.';
    }

    /*
     * Links
     */

    function beginLinkEdit(link) {
        editingPlacementId.value = link.Id;
        creatingLinkGroupId.value = null;
    }

    function cancelLinkEdit() {
        editingPlacementId.value = null;
    }

    function beginCreateLink(group) {
        creatingLinkGroupId.value = group.Id;
        editingPlacementId.value = null;
    }

    function cancelCreateLink() {
        creatingLinkGroupId.value = null;
    }

    async function moveLinkToGroup(group, link, newIndex) {
        
        clearError();

        normalizeLinkOrder(group);

        try {
            await requestJson(joinUrl(props.relatedLinksBaseUrl, `links/${link.Id}/move`), {
                method: 'PUT',
                body: {
                    DestinationGroupId: group.Id,
                    DestinationIndex: newIndex
                }
            });
        } catch (error) {
            setError(error);
        }
    }

    async function saveLink(
        group,
        link,
        data
    ) {
        clearError();
        savingLink.value = true;

        try {
            const updated = await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `links/${link.Id}`
                ),
                {
                    method: 'PUT',
                    body: data
                }
            );

            const index = group.Links.findIndex(
                x => x.Id === link.Id
            );

            group.Links.splice(
                index,
                1,
                updated
            );

            editingPlacementId.value = null;
        } catch (error) {
            setError(error);
        } finally {
            savingLink.value = false;
        }
    }

    async function createLink(
        group,
        data
    ) {
        clearError();
        savingLink.value = true;

        try {
            const created = await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `groups/${group.Id}/links`
                ),
                {
                    method: 'POST',
                    body: data
                }
            );

            group.Links.push(created);

            normalizeLinkOrder(group);

            creatingLinkGroupId.value = null;
        } catch (error) {
            setError(error);
        } finally {
            savingLink.value = false;
        }
    }

    async function deleteLink(
        group,
        link
    ) {
        if (!window.confirm(
            `Delete "${link.Text}"?`
        )) {
            return;
        }

        clearError();

        const index = group.Links.findIndex(
            x => x.Id === link.Id
        );

        const removed = group.Links.splice(
            index,
            1
        )[0];

        normalizeLinkOrder(group);

        try {
            await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `links/${link.Id}`
                ),
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            group.Links.splice(
                index,
                0,
                removed
            );

            normalizeLinkOrder(group);

            setError(error);
        }
    }

    function beginLinkDrag(group) {
        clearError();

        linkDragSnapshot = {
            groupId: group.Id,
            links: [...group.Links]
        };
    }

    async function endLinkDrag(group, event) {
        if (event.from !== event.to) {
            linkDragSnapshot = null;
            return;
        }

        if (event.oldIndex === event.newIndex) {
            linkDragSnapshot = null;
            return;
        }

        normalizeLinkOrder(group);

        try {
            await requestJson(joinUrl(props.relatedLinksBaseUrl, `groups/${group.Id}/links/order`), {
                method: 'PUT',
                body: {
                    PlacementIds: group.Links.map(x => x.Id)
                }
            });
        } catch (error) {
            if (linkDragSnapshot && linkDragSnapshot.groupId === group.Id) {
                group.Links.splice(0, group.Links.length, ...linkDragSnapshot.links);
                normalizeLinkOrder(group);
            }

            setError(error);
        } finally {
            linkDragSnapshot = null;
        }
    }

    function normalizeLinkOrder(group) {
        group.Links.forEach(
            (link, index) => {
                link.SortOrder = index;
            }
        );
    }

    /*
     * Groups
     */

    function beginGroupEdit(group) {
        editingGroupId.value = group.Id;
        groupNameDraft.value = group.Name ?? '';
    }

    function cancelGroupEdit() {
        editingGroupId.value = null;
        groupNameDraft.value = '';
    }

    async function saveGroupName(group) {
        clearError();

        try {
            const updated = await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `groups/${group.Id}`
                ),
                {
                    method: 'PUT',
                    body: {
                        Name: groupNameDraft.value
                    }
                }
            );

            group.Name = updated.Name;

            cancelGroupEdit();
        } catch (error) {
            setError(error);
        }
    }

    async function createGroup() {
        clearError();

        try {
            const created = await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `${widget.value.Id}/groups`
                ),
                {
                    method: 'POST',
                    body: {
                        Name: newGroupName.value
                    }
                }
            );

            created.Links ??= [];

            widget.value.Groups.push(created);

            normalizeGroupOrder();

            cancelCreateGroup();
        } catch (error) {
            setError(error);
        }
    }

    function cancelCreateGroup() {
        showCreateGroup.value = false;
        newGroupName.value = '';
    }

    async function deleteGroup(group) {
        const description = group.Name
            ? `"${group.Name}"`
            : 'this group';

        if (!window.confirm(
            `Delete ${description} and all of its links?`
        )) {
            return;
        }

        clearError();

        const index = widget.value.Groups
            .findIndex(x => x.Id === group.Id);

        const removed = widget.value.Groups.splice(
            index,
            1
        )[0];

        normalizeGroupOrder();

        try {
            await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `groups/${group.Id}`
                ),
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            widget.value.Groups.splice(
                index,
                0,
                removed
            );

            normalizeGroupOrder();

            setError(error);
        }
    }


    function beginGroupDrag() {
        clearError();

        groupDragSnapshot = [
            ...widget.value.Groups
        ];
    }

    async function endGroupDrag(event) {
        if (event.oldIndex === event.newIndex) {
            groupDragSnapshot = null;
            return;
        }

        normalizeGroupOrder();

        try {
            await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `${widget.value.Id}/groups/order`
                ),
                {
                    method: 'PUT',
                    body: {
                        GroupIds: widget.value.Groups.map(x => x.Id)
                    }
                }
            );
        } catch (error) {
            if (groupDragSnapshot) {
                widget.value.Groups.splice(
                    0,
                    widget.value.Groups.length,
                    ...groupDragSnapshot
                );

                normalizeGroupOrder();
            }

            setError(error);
        } finally {
            groupDragSnapshot = null;
        }
    }

    function normalizeGroupOrder() {
        widget.value.Groups.forEach(
            (group, index) => {
                group.SortOrder = index;
            }
        );
    }

    /*
     * Settings
     */

    function cancelSettings() {
        Object.assign(settingsDraft, {
            UploadFolder: 'pdfs',
            ...(widget.value.Settings ?? {})
        });
        showSettings.value = false;
    }

    async function saveSettings() {
        clearError();

        try {
            await requestJson(
                joinUrl(
                    props.relatedLinksBaseUrl,
                    `${widget.value.Id}/settings`
                ),
                {
                    method: 'PUT',
                    body: settingsDraft
                }
            );

            widget.value.Settings = { ...settingsDraft };

            showSettings.value = false;
        } catch (error) {
            setError(error);
        }
    }
</script>